import React from 'react';
import { Link2 } from 'lucide-react';
import { buildTimelineTracks, blockLabel } from './timelineTracks';

const TONE = {
  segment: 'bg-sky-200 border-sky-400 text-sky-900',
  gap: 'bg-[repeating-linear-gradient(135deg,#f1f5f9_0_6px,#e2e8f0_6px_12px)] border-slate-300 text-slate-500',
  worker: 'bg-emerald-200 border-emerald-400 text-emerald-900',
  open: 'bg-[repeating-linear-gradient(135deg,#fef3c7_0_6px,#fde68a_6px_12px)] border-amber-400 text-amber-900',
  shared: 'bg-violet-200 border-violet-400 text-violet-900',
  conflict: 'bg-red-200 border-red-500 text-red-900',
  rationeutral: 'bg-slate-200 border-slate-400 text-slate-800',
  ratioaccent: 'bg-violet-200 border-violet-400 text-violet-900',
  ratiodanger: 'bg-red-200 border-red-500 text-red-900',
};
const toneOf = (b) => (b.kind === 'ratio' ? TONE[`ratio${b.tone || 'neutral'}`] : TONE[b.kind] || TONE.segment);

/** UI-04 — four tracks, one axis, CSS blocks positioned by percentage; "+1" labels past midnight. Display only. */
export function ShiftTimeline({ shift, segments, group, assignedStaff, conflicts, serviceLabel }) {
  const { axis, tracks } = buildTimelineTracks({ shift, segments, group, assignedStaff, conflicts, serviceLabel });
  const span = Math.max(1, axis.end - axis.start);
  const pct = (m) => `${Math.max(0, Math.min(100, ((m - axis.start) / span) * 100))}%`;
  const width = (b) => `${Math.max(1, ((Math.min(b.end, axis.end) - Math.max(b.start, axis.start)) / span) * 100)}%`;
  return (
    <div className="rounded-lg border bg-card p-3 space-y-2" data-testid="shift-timeline">
      <div className="relative h-4 ml-28 text-[9px] text-muted-foreground">
        {axis.ticks.map((t) => <span key={t.at} className="absolute -translate-x-1/2 tabular-nums" style={{ left: pct(t.at) }}>{t.label}</span>)}
      </div>
      {tracks.map((track) => (
        <div key={track.key} className="space-y-1" data-testid={`timeline-track-${track.key}`}>
          {track.lanes.length === 0 && (
            <div className="flex items-center gap-2 text-[10px]"><span className="w-28 shrink-0 font-medium">{track.label}</span><span className="text-muted-foreground">—</span></div>
          )}
          {track.lanes.map((lane, li) => (
            <div key={`${track.key}-${li}`} className="flex items-center gap-2">
              <span className="w-28 shrink-0 text-[10px] leading-tight"><span className="font-medium">{li === 0 ? track.label : ''}</span>{li === 0 ? ' · ' : ''}<span className="text-muted-foreground truncate">{lane.label}</span></span>
              <div className="relative flex-1 h-5 rounded bg-slate-50 border border-slate-200 overflow-hidden">
                {axis.ticks.map((t) => <span key={t.at} className="absolute inset-y-0 border-l border-slate-200/80" style={{ left: pct(t.at) }} />)}
                {lane.blocks.map((b, bi) => (
                  <span key={bi} title={b.reason || blockLabel(b)} data-testid={`timeline-block-${b.kind}`}
                    className={`absolute inset-y-0 border rounded-sm text-[9px] px-1 flex items-center gap-0.5 overflow-hidden whitespace-nowrap ${toneOf(b)}`}
                    style={{ left: pct(b.start), width: width(b) }}>
                    {b.intentional && <Link2 className="w-2.5 h-2.5 shrink-0" />}
                    <span className="truncate">{b.label}{b.kind === 'conflict' && b.reason ? ` — ${b.reason}` : ''}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default ShiftTimeline;
